import type { Condition, FieldSpec, Values } from "../rules/types.js";
import { countryName, US_TERRITORIES } from "./countries.js";
import type { FormPack } from "./types.js";

/**
 * IRS Form W-8BEN, Rev. October 2021 — for individuals who are not U.S. persons.
 * Box names were read from the official PDF (https://www.irs.gov/pub/irs-pdf/fw8ben.pdf);
 * rules follow the form's instructions (https://www.irs.gov/instructions/iw8ben).
 */

const india: Condition = { field: "country", equals: "IN" };
const treaty: Condition = { field: "treaty", equals: true };
const mailing: Condition = { field: "mailSame", equals: false };
const PO_BOX = "\\b(p\\.?\\s*o\\.?\\s*box|post\\s*box|post\\s*office\\s*box|c\\/o|care\\s+of)\\b";

const fields: FieldSpec[] = [
  {
    id: "name", type: "text", label: "Full legal name",
    help: "As it appears on your passport or tax ID card.", autocomplete: "name",
    transforms: ["collapseSpaces", "trim"], meta: { line: "1" },
    rules: [
      { kind: "required", message: "Enter your full legal name." },
      { kind: "length", max: 60, message: "Keep it under 60 characters — use the name on your ID." },
      { kind: "check", check: "name.twoParts", severity: "warning" },
      { kind: "check", check: "sender.nameMatches", severity: "flag" },
    ],
  },
  {
    id: "citizenship", type: "country", label: "Country of citizenship", meta: { line: "2" },
    rules: [
      { kind: "required", message: "Choose your country of citizenship." },
      { kind: "notOneOf", values: ["US"], message: "U.S. citizens fill in Form W-9 instead of this form." },
    ],
  },
  {
    id: "street", type: "text", label: "Street address",
    help: "Where you live. No P.O. box or “in care of” address.", autocomplete: "address-line1",
    transforms: ["collapseSpaces", "trim"], meta: { line: "3" },
    rules: [
      { kind: "required", message: "Enter the street address where you live." },
      { kind: "notPattern", pattern: PO_BOX, flags: "i", message: "Use your home address. A P.O. box or “in care of” address isn't allowed here." },
      { kind: "length", max: 90, message: "Keep the street address under 90 characters." },
    ],
  },
  {
    id: "city", type: "text", label: "City or town", autocomplete: "address-level2",
    transforms: ["collapseSpaces", "trim"], meta: { line: "3" },
    rules: [{ kind: "required", message: "Enter your city or town." }],
  },
  {
    id: "state", type: "text", label: "State or province", autocomplete: "address-level1",
    transforms: ["collapseSpaces", "trim"], meta: { line: "3" },
    rules: [{ kind: "required", when: { field: "country", in: ["IN", "CA", "AU"] }, message: "Enter your state or province." }],
  },
  {
    id: "postal", type: "text", autocomplete: "postal-code", inputMode: "numeric",
    label: { default: "Postal code", cases: [{ when: india, text: "PIN code" }] },
    transforms: ["stripSpaces"], meta: { line: "3" },
    rules: [
      { kind: "required", when: india, message: "Enter your PIN code." },
      { kind: "pattern", when: india, pattern: "^[1-9]\\d{5}$", message: "An Indian PIN code has 6 digits, like 411001." },
      { kind: "length", max: 12, message: "That's too long for a postal code." },
    ],
  },
  {
    id: "country", type: "country", label: "Country", meta: { line: "3" },
    rules: [
      { kind: "required", message: "Choose the country where you live." },
      { kind: "notOneOf", values: ["US"], message: "A U.S. home address means this form doesn't apply. Use Form W-9 — or, if you live outside the U.S., choose that country." },
      { kind: "notOneOf", values: [...US_TERRITORIES], severity: "flag", message: "Home address is in a U.S. territory — check which form applies before relying on this one." },
    ],
  },
  { id: "mailSame", type: "toggle", label: "My mailing address is the same", defaultValue: true, meta: { line: "4" }, rules: [] },
  {
    id: "mstreet", type: "text", label: "Mailing street address", visibleWhen: mailing,
    transforms: ["collapseSpaces", "trim"], meta: { line: "4" },
    rules: [{ kind: "required", message: "Enter your mailing address, or switch on “same as above”." }],
  },
  {
    id: "mcity", type: "text", label: "City, state and postal code", visibleWhen: mailing,
    transforms: ["collapseSpaces", "trim"], meta: { line: "4" },
    rules: [{ kind: "required", message: "Enter the city, state and postal code." }],
  },
  {
    id: "mcountry", type: "country", label: "Country", visibleWhen: mailing, meta: { line: "4" },
    rules: [
      { kind: "required", message: "Choose the mailing address's country." },
      { kind: "notOneOf", values: ["US"], severity: "warning", message: "A U.S. mailing address is allowed, but you may be asked for proof that you live outside the U.S." },
    ],
  },
  {
    id: "ftin", type: "text", sensitive: true, autocomplete: "off",
    label: { default: "Foreign tax ID number", cases: [{ when: india, text: "PAN (Permanent Account Number)" }] },
    help: { default: "The tax number your country gave you.", cases: [{ when: india, text: "The 10-character number on your PAN card." }] },
    transforms: ["stripSpaces", "upper"], meta: { line: "6a" },
    hints: [
      { id: "letters", label: "5 letters", pattern: "^[A-Z]{5}", when: india },
      { id: "digits", label: "4 digits", pattern: "^[A-Z]{5}\\d{4}", when: india },
      { id: "last", label: "1 letter", pattern: "^[A-Z]{5}\\d{4}[A-Z]$", when: india },
      { id: "person", label: "4th letter P", pattern: "^[A-Z]{3}P", when: india },
    ],
    rules: [
      { kind: "required", when: { all: [treaty, { field: "ustin", empty: true }, { field: "ftinNotRequired", notEquals: true }] }, message: "A treaty claim needs a tax ID — enter your foreign tax ID or a U.S. SSN/ITIN." },
      { kind: "length", max: 0, when: { field: "ftinNotRequired", equals: true }, message: "Leave this blank, or switch off “my country doesn't issue tax numbers”." },
      { kind: "pattern", when: india, pattern: "^[A-Z]{5}\\d{4}[A-Z]$", message: "A PAN has 5 letters, 4 digits, then 1 letter — like ABCDE1234F." },
      { kind: "length", min: 4, max: 30, message: "That doesn't look like a tax ID number — check the length." },
      { kind: "check", check: "in.pan.holderType", when: india, severity: "warning" },
      { kind: "check", check: "in.pan.surnameInitial", when: india, severity: "warning" },
    ],
  },
  {
    id: "ftinNotRequired", type: "toggle", label: "My country doesn't issue tax ID numbers", defaultValue: false,
    visibleWhen: { field: "country", notIn: ["IN", ""] }, meta: { line: "6b" }, rules: [],
  },
  {
    id: "ustin", type: "text", sensitive: true, inputMode: "numeric", autocomplete: "off",
    label: "U.S. SSN or ITIN", help: "Most people leave this blank.",
    transforms: ["digitsAndDashes"], meta: { line: "5" },
    rules: [{ kind: "pattern", pattern: "^\\d{3}-?\\d{2}-?\\d{4}$", message: "An SSN or ITIN has 9 digits, like 123-45-6789." }],
  },
  {
    id: "dob", type: "date", sensitive: true, label: "Date of birth", help: "MM-DD-YYYY",
    placeholder: "MM-DD-YYYY", inputMode: "numeric", autocomplete: "bday",
    transforms: ["dateMDY"], meta: { line: "8" },
    rules: [{
      kind: "date", format: "MM-DD-YYYY",
      message: "Use MM-DD-YYYY, like 04-17-1996.",
      notFuture: "That date is in the future.",
      minAge: { years: 18, message: "You need to be 18 or older to sign this form." },
      maxAge: { years: 110, message: "Check the year — that's more than 110 years ago." },
    }],
  },
  { id: "treaty", type: "toggle", label: "I'm claiming a tax-treaty benefit", defaultValue: false, meta: { line: "9–10" }, rules: [] },
  {
    id: "tCountry", type: "country", label: "Country of tax residence", visibleWhen: treaty, meta: { line: "9" },
    rules: [
      { kind: "required", message: "Choose the country whose treaty you're claiming." },
      { kind: "notOneOf", values: ["US"], message: "The treaty country can't be the United States." },
      { kind: "check", check: "sameAs", params: { field: "country", message: "The treaty country is usually the country where you live (line 3)." }, severity: "warning" },
    ],
  },
  {
    id: "tArticle", type: "text", label: "Article and paragraph", visibleWhen: treaty,
    placeholder: "e.g. Article 15, paragraph 1", transforms: ["collapseSpaces", "trim"], meta: { line: "10" },
    rules: [{ kind: "required", message: "Enter the treaty article and paragraph." }, { kind: "length", max: 30, message: "Keep it short — the form has little room here." }],
  },
  {
    id: "tRate", type: "number", label: "Withholding rate (%)", visibleWhen: treaty, inputMode: "decimal",
    transforms: ["stripSpaces"], meta: { line: "10" },
    rules: [{ kind: "required", message: "Enter the rate you're claiming." }, { kind: "number", min: 0, max: 30, message: "Enter a rate from 0 to 30." }],
  },
  {
    id: "tIncome", type: "text", label: "Type of income", visibleWhen: treaty, transforms: ["collapseSpaces", "trim"], meta: { line: "10" },
    rules: [{ kind: "required", message: "Enter the type of income." }, { kind: "length", max: 90, message: "Keep it under 90 characters." }],
  },
  {
    id: "tCond", type: "textarea", label: "Conditions you meet", visibleWhen: treaty, transforms: ["collapseSpaces", "trim"], meta: { line: "10" },
    rules: [{ kind: "length", max: 110, message: "Keep it under 110 characters — the form has two short lines." }],
  },
];

function cityLine(v: Values): string {
  const city = String(v.city ?? "").trim();
  const state = String(v.state ?? "").trim();
  const postal = String(v.postal ?? "").trim();
  return [city, state].filter(Boolean).join(", ") + (postal ? ` ${postal}` : "");
}

const str = (x: unknown) => (typeof x === "string" ? x.trim() : "");

export const irsW8ben: FormPack = {
  id: "irs.w8ben",
  revision: "2021-10",
  title: "Form W-8BEN",
  issuer: "IRS",
  purpose: "Tells a U.S. payer you're not a U.S. person, so U.S. tax isn't withheld from what they pay you.",
  blankSha256: "b821dc1172c91b348a65675529cc792782f11fc1ae8579df92d627113203f918",
  blankFile: "irs-fw8ben-2021-10.pdf",
  acroPrefix: "topmostSubform[0].Page1[0].",
  fields,
  sections: [
    { id: "about", title: "About you", fields: ["name", "citizenship"] },
    { id: "home", title: "Where you live", fields: ["street", "city", "state", "postal", "country", "mailSame", "mstreet", "mcity", "mcountry"] },
    { id: "ids", title: "Tax numbers", fields: ["ftin", "ftinNotRequired", "ustin"] },
    { id: "birth", title: "Date of birth", fields: ["dob"] },
    { id: "treaty", title: "Tax treaty", optional: true, intro: "Most people working outside the U.S. leave this blank. Fill it in only if you're claiming a reduced rate under a tax treaty.", fields: ["treaty", "tCountry", "tArticle", "tRate", "tIncome", "tCond"] },
  ],
  boxes: (v) => {
    const mail = v.mailSame === false;
    const tr = v.treaty === true;
    const cond = str(v.tCond);
    return {
      f_1: str(v.name),
      f_2: countryName(str(v.citizenship)),
      f_3: str(v.street),
      f_4: cityLine(v),
      f_5: countryName(str(v.country)),
      f_6: mail ? str(v.mstreet) : "",
      f_7: mail ? str(v.mcity) : "",
      f_8: mail ? countryName(str(v.mcountry)) : "",
      f_9: str(v.ustin),
      f_10: str(v.ftin),
      c1_01: v.ftinNotRequired === true,
      f_11: "",
      f_12: str(v.dob),
      f_13: tr ? countryName(str(v.tCountry)) : "",
      f_14: tr ? str(v.tArticle) : "",
      f_15: tr ? str(v.tRate) : "",
      f_16: tr ? str(v.tIncome) : "",
      f_17: "",
      f_18: tr ? cond : "",
      // Electronic signing is only for the person named on line 1 — an agent with a power of attorney signs on paper.
      c1_02: false,
    };
  },
  // Read from the blank's widget rectangles (page 1, 612 × 792.008 pt).
  layout: {
    f_1: { page: 1, l: 5.88, t: 30.05, w: 55.29, h: 1.77 }, f_2: { page: 1, l: 61.5, t: 30.05, w: 32.61, h: 1.77 },
    f_3: { page: 1, l: 5.88, t: 33.08, w: 88.24, h: 1.77 }, f_4: { page: 1, l: 5.88, t: 36.11, w: 65.88, h: 1.77 },
    f_5: { page: 1, l: 72.09, t: 36.11, w: 22.03, h: 1.77 }, f_6: { page: 1, l: 5.88, t: 39.14, w: 88.24, h: 1.77 },
    f_7: { page: 1, l: 5.88, t: 42.17, w: 65.88, h: 1.77 }, f_8: { page: 1, l: 72.09, t: 42.17, w: 22.03, h: 1.77 },
    f_9: { page: 1, l: 10.59, t: 45.45, w: 83.53, h: 1.52 }, f_10: { page: 1, l: 5.88, t: 48.48, w: 41.18, h: 1.52 },
    c1_01: { page: 1, l: 92.09, t: 47.22, w: 1.31, h: 1.01 }, f_11: { page: 1, l: 5.88, t: 51.56, w: 41.18, h: 1.47 },
    f_12: { page: 1, l: 47.06, t: 51.52, w: 47.06, h: 1.51 }, f_13: { page: 1, l: 38.82, t: 54.54, w: 32.94, h: 1.52 },
    f_14: { page: 1, l: 10.59, t: 59.09, w: 18.22, h: 1.52 }, f_15: { page: 1, l: 57.6, t: 59.09, w: 3.58, h: 1.52 },
    f_16: { page: 1, l: 10.59, t: 60.61, w: 82.35, h: 1.52 }, f_17: { page: 1, l: 85.88, t: 62.12, w: 8.24, h: 1.52 },
    f_18: { page: 1, l: 10.59, t: 63.64, w: 83.53, h: 1.52 }, c1_02: { page: 1, l: 17.97, t: 86.62, w: 1.31, h: 1.01 },
    f_20: { page: 1, l: 17.65, t: 89.39, w: 51.76, h: 1.52 }, Date: { page: 1, l: 70.59, t: 89.39, w: 23.53, h: 1.52 },
    f_21: { page: 1, l: 17.65, t: 92.42, w: 51.76, h: 1.52 },
  },
  boxOf: {
    name: "f_1", citizenship: "f_2", street: "f_3", city: "f_4", state: "f_4", postal: "f_4", country: "f_5",
    mstreet: "f_6", mcity: "f_7", mcountry: "f_8", ustin: "f_9", ftin: "f_10", ftinNotRequired: "c1_01", dob: "f_12",
    tCountry: "f_13", tArticle: "f_14", tRate: "f_15", tIncome: "f_16", tCond: "f_18",
  },
  signature: { box: "f_20", dateBox: "Date", dateFormat: "MM-DD-YYYY", printNameBox: "f_21", printNameFrom: "name" },
  certification: {
    title: "Part III — Certification",
    paragraphs: [
      "Under penalties of perjury, I declare that I have examined the information on this form and to the best of my knowledge and belief it is true, correct, and complete. I further certify under penalties of perjury that:",
    ],
    bullets: [
      "I am the individual that is the beneficial owner (or am authorized to sign for the individual that is the beneficial owner) of all the income or proceeds to which this form relates or am using this form to document myself for chapter 4 purposes;",
      "The person named on line 1 of this form is not a U.S. person;",
      "This form relates to: (a) income not effectively connected with the conduct of a trade or business in the United States; (b) income effectively connected with the conduct of a trade or business in the United States but is not subject to tax under an applicable income tax treaty; (c) the partner’s share of a partnership’s effectively connected taxable income; or (d) the partner’s amount realized from the transfer of a partnership interest subject to withholding under section 1446(f);",
      "The person named on line 1 of this form is a resident of the treaty country listed on line 9 of the form (if any) within the meaning of the income tax treaty between the United States and that country; and",
      "For broker transactions or barter exchanges, the beneficial owner is an exempt foreign person as defined in the instructions.",
    ],
    closing: [
      "Furthermore, I authorize this form to be provided to any withholding agent that has control, receipt, or custody of the income of which I am the beneficial owner or any withholding agent that can disburse or make payments of the income of which I am the beneficial owner. I agree that I will submit a new form within 30 days if any certification made on this form becomes incorrect.",
    ],
    declaration: "By signing, you make the declaration above under penalties of perjury.",
  },
  validUntil: (signedAt) => `${signedAt.getUTCFullYear() + 3}-12-31`,
  // Golden Sparrow's rule (2026-10-24): a PAN / foreign tax ID and a date of birth on every W-8BEN.
  defaultPolicy: {
    alsoRequire: [{ field: "ftin", when: { field: "ftinNotRequired", notEquals: true } }, "dob"],
    requiredMessages: { ftin: "Enter your tax ID number — for India, your PAN.", dob: "Enter your date of birth." },
  },
  stopWhen: [
    { when: { field: "citizenship", equals: "US" }, message: "U.S. citizens fill in Form W-9 instead." },
    { when: { field: "country", equals: "US" }, message: "If you live in the U.S., Form W-9 is the right form." },
  ],
};
